import { Injectable } from "@angular/core";
import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { NetworkExternalConfiguration } from "../../models/domains/pbm/network-term/network-external-configuration.model";
import { BackendUrl } from "../../models/domains/urls/back-end.url";
import { NetworkFinancialStatementPagingRequest } from "../../pages/network-financial-statement/network-financial-statement-paging-request";
import { UserRoleMediator } from "../mediators/user-role.mediator";
import { NetworkExternalConfigurationParse } from "../parsers/pbm/network/network-external-configuration.parser";
import { SearchNetworkFinancialStatementsParse } from "../parsers/pbm/network/search-network-financial-statement.parse";

export interface NetworkDto {
  CorporateName: string;
  DisplayName: string;
  TradingName: string;
  FiscalNumber: {
    Value: string;
  };
}
export interface SearchNetworksResponse {
  Start: number;
  Total: number;
  TotalPerPages: number;
  Data: NetworkDto[];
}

@Injectable()
export class NetworkRepository {
  private headers: any;
  private fiscalNumberRootHeader: any;

  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private userRoleMediator: UserRoleMediator,
    private searchNetworkFinancialStatementsParse: SearchNetworkFinancialStatementsParse,
    private networkExternalConfigurationParse: NetworkExternalConfigurationParse,
  ) {
    this.headers = {
      "Content-Type": "application/json",
    };
    this.fiscalNumberRootHeader = {
      "Content-Type": "application/json",
      "X-NetworkFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
  }

  public async searchNetworkFinancialStatements(pagingRequest: NetworkFinancialStatementPagingRequest): Promise<PageableResult> {
    const body = {
      FilterText: pagingRequest.getFiscalNumber() ? pagingRequest.getFiscalNumber() : null,
      ReferenceYear: pagingRequest.getReferenceYear(),
      ReferenceMonth: pagingRequest.getReferenceMonth(),
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
    };
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/network/financial-statements"), body, this.fiscalNumberRootHeader);
      return this.searchNetworkFinancialStatementsParse.parse(data);
    } catch (httpErrorResponse) {
      return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
    }
  }

  public async findNetworkExportOrderKey(fiscalNumber: string, startAt: string, endAt: string): Promise<ExportOrderKey> {
    const body = JSON.stringify({
      FiscalNumber: {
        Value: fiscalNumber,
      },
      StartAt: startAt,
      EndAt: endAt,
    });
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/network/financial-statement"), body, this.headers);
      return new ExportOrderKey(data.toString());
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getExternalNetworkConfiguration(fiscalNumber: string): Promise<NetworkExternalConfiguration> {
    try {
      const data = await this.remoteGatewayFactory.createDefaultRemoteGateway().get(new BackendUrl("/api/external-network-configuration/" + fiscalNumber));
      return this.networkExternalConfigurationParse.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async enableNetworkPricingConsult(fiscalNumber: string): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    const body = null;

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .putWithHeaders(new BackendUrl("/api/external-network-configuration/EnableNetworkPricingConsult/" + fiscalNumber), body, headers);
      return data;
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async disableNetworkPricingConsult(fiscalNumber: string): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    const body = null;

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .putWithHeaders(new BackendUrl("/api/ExternalNetworkConfiguration/DisableNetworkPricingConsult/" + fiscalNumber), body, headers);
      return data;
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async exportLpmStatement(body: any): Promise<any> {
    const headers = { "Content-Type": "application/json" };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/network/financial-statements/industry/export"), body, headers);
      return data;
    } catch (httpErrorResponse) {
      return await Promise.reject(httpErrorResponse.error?.Data?.ErrorMessage || httpErrorResponse.message);
    }
  }
}
